/** Project header: "Mark complete" (pick the completion date, 10% holdback starts its 12-month clock) / reopen / release. */
import { useState } from "react";
import { CheckCircle2, RotateCcw, BadgeDollarSign, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function CompleteProjectButton({ project, onChanged }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const pct = Number(project.value || 0) * 0.1;

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setOpen(false); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };

  if (project.completed_at) {
    const released = project.holdback_released_at;
    return (
      <span className="mr-1 inline-flex flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="project-completed-badge">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Completed {project.completed_at}
        <span className="text-gray-400">·</span>
        <span className={released ? "text-emerald-700" : "text-amber-700"}>holdback {money(project.holdback_amount)} {released ? `released ${released}` : `matures ${project.holdback_matures_at}`}</span>
        {project.holdback_invoice ? (
          <a href={project.holdback_invoice.link} target="_blank" rel="noreferrer" className="rounded-md border border-sky-300 bg-sky-50 px-2 py-1 text-sky-800 hover:bg-sky-100" data-testid="project-holdback-invoice">
            <FileText className="mr-1 inline h-3 w-3" />QB #{project.holdback_invoice.doc_number || project.holdback_invoice.qbo_id}
          </a>
        ) : !released && (
          <button onClick={() => run(() => api.post(`/projects/${project.project_id}/holdback/bill`), "Holdback invoice drafted in QuickBooks")} disabled={busy}
            className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800 hover:bg-amber-100 disabled:opacity-50" data-testid="project-holdback-bill">
            <FileText className="mr-1 inline h-3 w-3" />Bill for release
          </button>
        )}
        {!released && (
          <button onClick={() => run(() => api.post(`/projects/${project.project_id}/holdback/release`, {}), "Holdback marked released")} disabled={busy}
            className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50" data-testid="project-holdback-release">
            <BadgeDollarSign className="mr-1 inline h-3 w-3" />Released
          </button>
        )}
        <button onClick={() => run(() => api.post(`/projects/${project.project_id}/reopen`), "Project reopened")} disabled={busy}
          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-50" data-testid="project-reopen">
          <RotateCcw className="mr-1 inline h-3 w-3" />Reopen
        </button>
      </span>
    );
  }
  return (
    <span className="relative mr-1">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100" data-testid="project-complete-btn">
        <CheckCircle2 className="h-3.5 w-3.5" /> Complete
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-72 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700 shadow-xl" data-testid="project-complete-popover">
          <p className="font-semibold text-gray-900">Mark this job complete</p>
          <label className="mt-2 block text-[10px] font-mono uppercase tracking-widest text-gray-500">Completion date</label>
          <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1" data-testid="project-complete-date" />
          <p className="mt-2 text-gray-600">Holdback <b>{money(pct)}</b> (10% of {money(project.value)}) matures 12 months later. Admins get reminded 2 months and 2 weeks before, and on the day.</p>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="rounded-md border border-gray-200 px-2.5 py-1">Cancel</button>
            <button onClick={() => run(() => api.post(`/projects/${project.project_id}/complete`, { completed_at: date }), "Job marked complete — holdback clock started")} disabled={busy || !date}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 font-semibold text-white disabled:opacity-50" data-testid="project-complete-confirm">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Complete
            </button>
          </div>
        </div>
      )}
    </span>
  );
}
